import React, { useState, useEffect, useRef, useCallback } from "react";
import _ from "lodash";
import clsx from "clsx";

import { TextField, InputAdornment } from "@material-ui/core";
import { Search, Check } from "lucide-react";

import useCurrentEnvironment from "../hooks/use-environment";

interface Environment {
  ID: string | null;
  accountID: string | null;
  name: string;
}

interface Project {
  ID: string | null;
  accountID: string | null;
  environments: Environment[];
  name: string;
}

interface EnvironmentPickerPanelProps {
  projects: Project[];
  setEnvironmentAndProject: (
    environment: Environment,
    project?: Project,
  ) => void;
  onClose?: () => void;
}

export const EnvironmentPickerPanel = React.forwardRef<
  HTMLDivElement,
  EnvironmentPickerPanelProps
>(({ projects, setEnvironmentAndProject, onClose }, ref) => {
  const currentEnvironment = useCurrentEnvironment();
  const [searchString, setSearchString] = useState("");
  const [projectsToShow, setProjectsToShow] = useState<Project[]>(projects);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Find first focusable nav item after the environment picker
  const findFirstNavItem = useCallback((): HTMLElement | null => {
    const nav = document.querySelector("nav");
    if (!nav) return null;
    return nav.querySelector<HTMLElement>("button, a");
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      } else if (e.key === "Tab" && !e.shiftKey) {
        const container = containerRef.current;
        if (container) {
          const focusables = container.querySelectorAll<HTMLElement>(
            'input, [tabindex="0"]',
          );
          const lastFocusable = focusables[focusables.length - 1];

          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            onClose?.();
            const firstNavItem = findFirstNavItem();
            if (firstNavItem) {
              firstNavItem.focus();
            }
          }
        }
      }
    },
    [onClose, findFirstNavItem],
  );

  const envRowClasses = (environment: Environment): string =>
    clsx(
      "flex justify-between items-center px-4 py-2 text-textDefault text-sm hover:bg-surface-hover cursor-pointer truncate",
      {
        "bg-surface-selected":
          currentEnvironment && currentEnvironment.ID === environment.ID,
      },
    );

  useEffect(() => {
    const projs: Project[] = JSON.parse(JSON.stringify(projects));
    setProjectsToShow(
      projs
        .map((proj: Project) => {
          const envs = proj.environments.filter((env: Environment) => {
            // perform filtering at env level checking both project and
            // env name
            return (
              proj.name.toLowerCase().includes(searchString) ||
              env.name.toLowerCase().includes(searchString)
            );
          });
          proj.environments = _.sortBy(envs, "createdAt");
          return proj;
        })
        // filter out any projects without env/proj matches
        .filter((proj: Project) => proj.environments.length > 0)
        .sort((a: Project, b: Project) => a.name.localeCompare(b.name)),
    );
  }, [searchString, projects]);

  // Combine refs
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [ref],
  );

  return (
    <div
      ref={setRefs}
      className="border-interactive-selected bg-surface-default flex max-h-[calc(100vh-75px)] w-80 flex-col gap-3 overflow-y-auto rounded-lg border pt-1 pb-4 shadow-md"
      onKeyDown={handleKeyDown}
    >
      <TextField
        className="!border-borderDefault w-full !border-b !p-3"
        name="search"
        value={searchString}
        variant="outlined"
        inputRef={searchInputRef}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearchString(e.target.value.toLowerCase())
        }
        InputProps={{
          placeholder: "Search environments/projects",
          className: "!rounded-md",
          startAdornment: (
            <InputAdornment position="start">
              <Search size={20} />
            </InputAdornment>
          ),
        }}
      />
      {projectsToShow.length === 0 ? (
        <div className="text-textSubtle truncate px-4 text-xs font-bold">
          No projects or environments found
        </div>
      ) : (
        projectsToShow.map((project: Project, index: number) => (
          <div key={project.ID}>
            <div className="text-textSubtle mb-2 truncate px-4 text-xs font-bold">
              {project.name}
            </div>
            {project.environments.map((environment: Environment) => (
              <div
                key={environment.ID}
                role="option"
                tabIndex={0}
                aria-selected={
                  currentEnvironment?.ID === environment.ID || undefined
                }
                onClick={() => setEnvironmentAndProject(environment, project)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setEnvironmentAndProject(environment, project);
                  }
                }}
                className={clsx(
                  envRowClasses(environment),
                  "focus:bg-surface-hover focus:outline-none",
                )}
              >
                {environment.name}
                {currentEnvironment &&
                  currentEnvironment.ID === environment.ID && (
                    <Check className="text-textActionSelected mr-2" />
                  )}
              </div>
            ))}
            {index !== projectsToShow.length - 1 && (
              <div className="w-full border-b" />
            )}
          </div>
        ))
      )}
    </div>
  );
});
